import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCalendarAlt, FaClock, FaChartLine, FaUsers, FaMoneyBillWave, FaStar, FaClipboardList } from 'react-icons/fa';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

import profileImg from '../../assets/team.png';
import './expert.css';

export default function ExpertOverview() {
  const { user } = useAuth();
  const [overviewData, setOverviewData] = useState(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get user's display name
  const getUserName = () => {
    if (user && user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user && user.name) {
      return user.name;
    }
    return "Expert";
  };

  // Fetch expert overview data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch overview and appointments in parallel
        const [overviewResponse, appointmentsResponse] = await Promise.all([
          authAPI.getExpertOverview(),
          authAPI.getExpertUpcomingAppointments()
        ]);

        if (overviewResponse.success) {
          setOverviewData(overviewResponse.data);
        }

        if (appointmentsResponse.success) {
          setUpcomingAppointments(appointmentsResponse.data);
        }
      } catch (err) {
        console.error('Error fetching expert data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Format currency with Nigerian naira symbol
  const formatCurrency = (amount) => {
    return `₦${amount?.toLocaleString() || '0'}`;
  };

  // Format date and time
  const formatDateTime = (dateString, timeString) => {
    const date = new Date(dateString);
    const time = new Date(timeString);
    
    const dateOptions = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
    const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    
    return {
      date: date.toLocaleDateString('en-US', dateOptions),
      time: time.toLocaleTimeString('en-US', timeOptions)
    };
  };

  // Statistics data based on API response
  const stats = overviewData ? [
    { 
      id: 1, 
      title: 'Total Clients', 
      value: overviewData.total_clients || 0, 
      icon: <FaUsers className="stat-icon-expt" />,
      positive: true
    },
    { 
      id: 2, 
      title: 'Appointments', 
      value: overviewData.total_appointments || 0, 
      icon: <FaCalendarAlt className="stat-icon-expt" />,
      positive: true
    },
    { 
      id: 3, 
      title: 'Total Earnings', 
      value: formatCurrency(overviewData.total_earnings), 
      icon: <FaMoneyBillWave className="stat-icon-expt" />,
      positive: true
    },
  
  ] : [];

  // Loading component
  if (loading) {
    return (
      <div className="expert-dashboard-container-expt">
        <div className="loading-container-expt">
          <div className="loading-spinner-expt"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Error component
  if (error) {
    return (
      <div className="expert-dashboard-container-expt">
        <div className="error-container-expt">
          <p className="error-message-expt">{error}</p>
          <button 
            className="retry-btn-expt" 
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="expert-dashboard-container-expt">
      {/* Welcome section */}
      <div className="welcome-section-expt">
        <div className="welcome-text-expt">
          <h1>Welcome back, {getUserName()}!</h1>
          <p>Here's an overview of your expert dashboard</p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="stats-overview-expt">
        <div className="section-header-expt">
          <h2>Performance Overview</h2>
        </div>
        <div className="stats-cards-expt">
          {stats.map(stat => (
            <div className="stat-card-expt" key={stat.id}>
              <div className="stat-icon-wrapper-expt">
                {stat.icon}
              </div>
              <div className="stat-info-expt">
                <h3>{stat.title}</h3>
                <div className="stat-details-expt">
                  <span className="stat-value-expt">{stat.value}</span>
                  <span className={`stat-change-expt ${stat.positive ? 'positive-expt' : 'negative-expt'}`}>
                    {stat.change}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="upcoming-appointments-expt">
        <div className="section-header-expt">
          <h2>Upcoming Appointments</h2>
          <Link to="/expert-dashboard/appointments" className="view-all-link-expt">
            View All
          </Link>
        </div>

        <div className="appointments-cards-expt">
          {upcomingAppointments.length > 0 ? (
            upcomingAppointments.map(appointment => {
              const { date, time } = formatDateTime(appointment.date, appointment.time);
              return (
                <div className="expert-appointment-card-expt" key={appointment.appointment_id}>
                  <div className="appointment-client-info-expt">
                    <img 
                      src={appointment.client_image || profileImg} 
                      alt={appointment.client_name} 
                      className="client-image-expt" 
                    />
                    <div className="client-details-expt">
                      <h3>{appointment.client_name}</h3>
                      <p className="appointment-topic-expt">{appointment.description}</p>
                    </div>
                  </div>

                  <div className="appointment-time-details-expt">
                    <div className="appointment-detail-expt">
                      <FaCalendarAlt className="detail-icon-expt" />
                      <span>{date}</span>
                    </div>
                    <div className="appointment-detail-expt">
                      <FaClock className="detail-icon-expt" />
                      <span>{time}</span>
                    </div>
                    <div className="appointment-detail-expt">
                      <span className="consultation-type-expt">
                        {appointment.consultation_type === 'video' ? '📹 Video' : '📞 Voice'}
                      </span>
                    </div>
                  </div>

                  <div className="appointment-actions-expt">
                    <Link to={`/expert-dashboard/appointments/${appointment.appointment_id}`} className="view-details-btn-expt">
                      View Details
                    </Link>
                    <Link to={`/expert-dashboard/messages`} className="contact-client-btn-expt">
                      Message Client
                    </Link>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-appointments-expt">
              <p>No upcoming appointments scheduled</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}